import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Lock, ArrowRight, Loader2, UserPlus, LogIn } from "lucide-react";
import { Logo } from "../components/Logo";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const redirectTo = searchParams.get("redirect") || "/meine-seiten";
  const pageId = searchParams.get("page_id") || "";

  const [mode, setMode] = useState(searchParams.get("mode") === "login" ? "login" : "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Bitte alle Felder ausfüllen"); return; }
    if (password.length < 6) { toast.error("Passwort muss mindestens 6 Zeichen haben"); return; }

    setLoading(true);
    try {
      if (mode === "register") {
        await register(email, password, pageId);
        toast.success("Account erstellt!");
      } else {
        await login(email, password);
        toast.success("Erfolgreich angemeldet!");
      }
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Anmeldung");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <Logo size="lg" clickable={false} />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
          <div className="flex rounded-xl bg-slate-100 p-1 mb-8">
            <button data-testid="tab-register"
              onClick={() => setMode("register")}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${mode === "register" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
              <UserPlus className="w-4 h-4 inline mr-1.5" />Registrieren
            </button>
            <button data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${mode === "login" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
              <LogIn className="w-4 h-4 inline mr-1.5" />Anmelden
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="auth-email" id="email" type="email" placeholder="name@firma.de" value={email}
                  onChange={e => setEmail(e.target.value)} className="pl-10 h-11 rounded-xl" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-1.5 block">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="auth-password" id="password" type="password" placeholder="Mindestens 6 Zeichen" value={password}
                  onChange={e => setPassword(e.target.value)} className="pl-10 h-11 rounded-xl" />
              </div>
            </div>
            <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === "register" ? "Account erstellen" : "Anmelden"} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === "register" ? "Bereits ein Konto?" : "Noch kein Konto?"}
            <button onClick={() => setMode(mode === "register" ? "login" : "register")} className="ml-1 text-indigo-600 font-medium hover:underline">
              {mode === "register" ? "Anmelden" : "Registrieren"}
            </button>
          </p>
          {mode === "login" && (
            <p className="text-center text-sm mt-3">
              <Link to="/passwort-vergessen" data-testid="forgot-password-link" className="text-slate-400 hover:text-indigo-600 transition-colors">
                Passwort vergessen?
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
